import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    PrSalaryComponentEntity,
    PrSalaryComponentTemporalEntity,
    PrSalaryComponentTypeEntity
} from "@odata/GeneratedEntityTypes";
import { transformToODataString } from "@odata/OData.utils";
import { getTemporalAdditionalProperties, TemporalValidityFieldsDef } from "@odata/TemporalUtils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import i18next from "i18next";

import { Sort, ValueType } from "../../../enums";
import { Model } from "../../../model/Model";
import { formatCurrency } from "../../../types/Currency";
import { IFormDef } from "../../../views/formView/Form";
import { ISplitPageTableDef, ITableDefTabData } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import SalaryComponentFormView from "./SalaryComponentFormView";
import {
    getSalaryComponentColumns,
    getSalaryComponentFieldDefinitions,
    getSalaryEnumNamespaceNames,
    SalaryComponentVariant
} from "@pages/payroll/salaryComponent/SalaryComponent.utils";

export const getDefinitionsFactory = (inEditableWindow?: boolean, selectedIds?: number[]): IGetDefinition => {
    const getDefinitions: IGetDefinition = (): IDefinition => {

        const preventStoreVariant = inEditableWindow;

        let filter: string;
        if (selectedIds?.length) {
            filter = `not(${PrSalaryComponentEntity.Id} in (${transformToODataString(selectedIds, ValueType.Number)}))`;
        }

        const defaultTabs: ITableDefTabData[] = [
            { id: "1", title: i18next.t("SalaryComponent:Tabs.Active") },
            { id: "2", title: i18next.t("SalaryComponent:Tabs.Inactive") },
            { id: null, title: i18next.t("SalaryComponent:Tabs.All") }
        ];
        const tabs: ITableDefTabData[] = inEditableWindow ? null : defaultTabs;

        const additionalProperties = !inEditableWindow ? null
                : getSalaryComponentColumns(SalaryComponentVariant.Employment)
                        .map(col => ({ id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, col) }));

        const table: ISplitPageTableDef = {
            id: `${EntityTypeName.PrSalaryComponent}Table`,
            preventStoreVariant,
            filterBarDef: [{
                ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
                defaultFilters: [
                    createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name),
                    createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Type),
                    createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)
                ],
                filterDefinition: {
                    [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Type)]: {
                        fieldSettings: {
                            displayName: PrSalaryComponentTypeEntity.Name
                        }
                    },
                    [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name)]: {},
                    [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)]: {}
                },
                isValueHelp: true
            }],
            filter,
            initialSortBy: [{
                id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Type),
                sort: Sort.Desc
            }],
            columns: [
                createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name),
                createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Type),
                createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)
            ],
            additionalProperties,
            columnDefinition: {
                [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Type)]: {
                    fieldSettings: {
                        displayName: PrSalaryComponentTypeEntity.Name
                    }
                },
                [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name)]: {},
                [createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount)]: {
                    formatter: (val, args) => {
                        return formatCurrency(val, getCompanyCurrency(args.context));
                    }
                }
            },
            title: i18next.t("SalaryComponent:Title"),
            tabs,
            tabsSettings: {}
        };
        const summary: ISummaryItem[] = [];

        const form: IFormDef = {
            id: `${EntityTypeName.PrSalaryComponent}Form`,
            translationFiles: getDefinitions.translationFiles,
            getItemBreadCrumbText: (storage: Model) =>
                    getItemBreadCrumbsText(storage, i18next.t("SalaryComponent:New"), i18next.t("SalaryComponent:FormTitle")),
            isDeletable: true,
            formControl: SalaryComponentFormView,
            summary,
            additionalProperties: [
                ...getTemporalAdditionalProperties(PrSalaryComponentTemporalEntity, PrSalaryComponentEntity)
            ],
            fieldDefinition: {
                ...TemporalValidityFieldsDef,
                ...getSalaryComponentFieldDefinitions()
            },
            groups: [
                {
                    id: "main",
                    rows: [
                        [
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Name) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Type) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.RegularSalaryType) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.DefaultPercentage) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.FixedBonusTaxImpact) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.FixedBonusPayOutImpact) },
                            { id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.Amount) }
                        ]

                    ]
                }
            ]
        };
        return {
            entitySet: EntitySetName.PrSalaryComponents,
            table,
            form
        };
    };

    getDefinitions.translationFiles = ["SalaryComponent", "Common", ...getSalaryEnumNamespaceNames()];

    return getDefinitions;
};

export const getDefinitions = getDefinitionsFactory();

setDefByEntityType(EntityTypeName.PrSalaryComponent, getDefinitions);